import { agentsApi } from '../../../data/agentsApi';
import type { AgentDefinitionView, AgentHealthView } from '../../../data/views';
import type { AgentDetailView } from './detailViews';

/**
 * Detail feature endpoints, injected into the single agents base api.
 *
 * Self-contained: detail owns every read its page needs — the header (`getAgentDetail`), the recipe
 * definition (`getAgentDefinition`), and health (`getAgentHealth`). The definition and health reads are
 * deliberately DUPLICATED here (the stepRecipe and runs features inject their own copies under their own
 * names). Because tag types are shared on the base api, a write in another feature still invalidates
 * these via tags; the only cost is no request dedup across features (fine for these admin screens).
 */
export const detailEndpoints = agentsApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgentDetail: builder.query<AgentDetailView, { agentKey: string; company?: string }>({
      query: ({ agentKey, company }) => ({
        url: `/agents/${agentKey}`,
        method: 'GET',
        params: company ? { company } : undefined,
      }),
      providesTags: (_result, _error, { agentKey }) => [{ type: 'AgentDetail', id: agentKey }],
    }),
    getAgentDefinition: builder.query<AgentDefinitionView, { agentKey: string; company?: string }>({
      query: ({ agentKey, company }) => ({
        url: `/agents/${agentKey}/definition`,
        method: 'GET',
        params: company ? { company } : undefined,
      }),
      providesTags: (_result, _error, { agentKey }) => [{ type: 'StepRecipe', id: agentKey }],
    }),
    getAgentHealth: builder.query<AgentHealthView, { agentKey: string; company?: string }>({
      query: ({ agentKey, company }) => ({
        url: `/agents/${agentKey}/health`,
        method: 'GET',
        params: company ? { company } : undefined,
      }),
      providesTags: (_result, _error, { agentKey }) => [{ type: 'AgentHealth', id: agentKey }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAgentDetailQuery,
  useGetAgentDefinitionQuery,
  useGetAgentHealthQuery,
} = detailEndpoints;
